import moment from 'moment-timezone';
import { IBooking, IBookingLock } from 'shared-types/index';
import ManagerService from 'external-services/manager-service/manager.service';
import { cloneDeep } from 'lodash';

//This service deals with validating server side locks placed on bookings.
class BookingLockService {
  readonly BookingLockedMessage = 'Booking is locked during user payment. Please wait.';
  readonly PaymentPendingLockName = 'payment_pending';

  //Returns true if booking is locked for edit or viewing.
  public isBookingLocked(booking: IBooking): boolean {
    if (!booking.locks) {
      return false;
    }

    for (const lock of booking.locks) {
      if (this.isLocked(lock)) {
        return true;
      }
    }
    return false;
  }

  // Return booking.locked
  public isBookingLockedByManagement(booking: IBooking): boolean {
    return booking.locked;
  }

  // Callback when unlock/lock a booking
  public toggleLockBookingForManagement(
    booking: IBooking,
    callBack?: (locked: boolean) => void
  ): void {
    if (booking.locked && ManagerService.isIncludedAdminRole) {
      booking.locked = false;
    } else if (!booking.locked) {
      booking.locked = true;
    }

    if (callBack) callBack(booking.locked);
  }

  //Returns null if booking isn't locked by given named lock
  //Returns the lock instance otherwise.
  public isBookingLockedBy(name: string, booking: IBooking): IBookingLock {
    if (!booking.locks) {
      return null;
    }

    const lock = booking.locks.find((x) => x.name === name.toLowerCase());

    if (lock && this.isLocked(lock)) {
      return lock;
    } else {
      return null;
    }
  }

  //Checks if a lock is valid.
  private isLocked(lock: IBookingLock): boolean {
    if (moment.utc().isBefore(lock.expiresUtc)) {
      return true;
    } else {
      return false;
    }
  }

  public checkLockedPendingPayment(booking: IBooking): ILockCheckResult {
    //Legacy expiry times
    const timeLimitInMinutes: number = 15; // default
    const timeLimitForPayment: number = timeLimitInMinutes * 60 * 1000; // X minutes calculated to miliseconds {Default: 660000} => 11 min
    const bookingTimeSpan: number = Date.now() - Number(new Date(booking.bookedAt));

    let lockedMessage = this.BookingLockedMessage;
    const paymentLock = this.isBookingLockedBy(this.PaymentPendingLockName, booking);
    let isLockedForView = false;
    if (paymentLock) {
      //If this is true the booking supports the new locking mechanism
      lockedMessage = paymentLock.reason; //Use the server provided reason.
      isLockedForView = true;
    } else {
      //Apply legacy locking until deprecated
      isLockedForView =
        booking.method == 'online' &&
        booking.status.code == 'P$' &&
        booking._rev.slice(0, 2) == '1-' &&
        !booking.movedFromBookingId &&
        bookingTimeSpan < timeLimitForPayment;
    }

    return {
      isLocked: isLockedForView,
      reason: lockedMessage,
    };
  }
}

/**
 * This interface is used to faciliate checks for a named lock that's compatible with existing code
 */
export interface ILockCheckResult {
  isLocked: boolean;
  reason: string;
}

const instance = new BookingLockService();
export default instance as BookingLockService;
